import { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Upload, Plus, Trash2, EyeOff, Pencil, Shield, Check, Users, FileText, Bell, Send, Store, MapPin } from "lucide-react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CUISINES } from "../lib/cuisines";
import { AddressAutocomplete } from "../components/AddressAutocomplete";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const STATUS_STYLE = { published: "bg-fresh-mint/15 text-fresh-mint", pending: "bg-street-gold/15 text-street-gold", unpublished: "bg-coral-twist/15 text-coral-twist" };
const STATUS_LABEL = { published: "Publié", pending: "En attente", unpublished: "Dépublié" };
const PLAN_STYLE = { proplus: "bg-street-gold/20 text-street-gold", pro: "bg-fresh-mint/20 text-fresh-mint", free: "bg-cream-bg text-urban-teal/60" };
const TABS = [
  { key: "fiches", label: "Fiches & Imports", Icon: Store },
  { key: "imports", label: "Historique CSV", Icon: FileText },
  { key: "users", label: "Clients & Forfaits", Icon: Users },
  { key: "notifications", label: "Notifications", Icon: Bell },
];

export default function Admin() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("fiches");
  const [trucks, setTrucks] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [newT, setNewT] = useState({ name: "", city: "", cuisine: "Burger", description: "", photo: "" });
  const [edit, setEdit] = useState(null);
  const [users, setUsers] = useState([]);
  const [imports, setImports] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [bc, setBc] = useState({ audience: "foodies", slug: "", subject: "", message: "" });

  useEffect(() => { if (!loading && (!user || user.role !== "admin")) nav("/"); }, [user, loading, nav]);
  const reload = () => api.get("/admin/trucks").then((r) => setTrucks(r.data)).catch((e) => toast.error(apiError(e)));
  const loadUsers = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  const loadImports = () => api.get("/admin/imports").then((r) => setImports(r.data)).catch(() => {});
  const loadNotifs = () => api.get("/admin/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  useEffect(() => { if (user?.role === "admin") { reload(); loadUsers(); loadImports(); loadNotifs(); } }, [user]);

  const setPlan = async (uid, plan) => {
    try { await api.post(`/admin/users/${uid}/plan?plan=${plan}`); toast.success("Forfait mis à jour"); loadUsers(); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const sendBroadcast = async () => {
    if (!bc.subject || !bc.message) return toast.error("Sujet et message requis");
    if (bc.audience === "truck" && !bc.slug) return toast.error("Choisissez un foodtruck");
    try { const { data } = await api.post("/admin/notifications/broadcast", bc); toast.success(`Notification envoyée à ${data.recipients} destinataire(s)`); setBc({ ...bc, subject: "", message: "" }); loadNotifs(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const shown = useMemo(() => (statusFilter === "all" ? trucks : trucks.filter((t) => t.status === statusFilter)), [trucks, statusFilter]);
  const fmt = (s) => (s ? new Date(s).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "-");

  const create = async () => {
    if (!newT.name || !newT.city) return toast.error("Nom et ville requis");
    try { await api.post("/admin/trucks", newT); toast.success("Fiche créée (en attente de validation)"); setNewT({ name: "", city: "", cuisine: "Burger", description: "", photo: "" }); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const setStatus = async (t, status) => {
    try { await api.post(`/admin/trucks/${t.id}/status?status=${status}`); toast.success(`Fiche ${STATUS_LABEL[status].toLowerCase()}`); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (t) => { if (!window.confirm(`Supprimer ${t.name} ?`)) return; try { await api.delete(`/admin/trucks/${t.id}`); toast.success("Supprimé"); reload(); } catch (e) { toast.error(apiError(e)); } };
  const saveEdit = async () => {
    try { await api.put(`/admin/trucks/${edit.id}`, { name: edit.name, city: edit.city, cuisine: edit.cuisine, description: edit.description }); toast.success("Modifié"); setEdit(null); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const importCsv = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/admin/import-csv", fd); toast.success(`${data.created} fiche(s) importée(s) en attente`); reload(); }
    catch (err) { toast.error(apiError(err)); }
    e.target.value = "";
  };

  if (loading || user?.role !== "admin") return <p className="text-center py-24 text-urban-teal/50">Chargement...</p>;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="text-3xl font-display font-extrabold text-urban-teal flex items-center gap-2"><Shield className="text-street-gold" /> Back-office admin</h1>
      <p className="text-urban-teal/60">Gérez toutes les fiches, importez et validez avant publication.</p>

      <div className="flex gap-2 overflow-x-auto no-scrollbar mt-5">
        {TABS.map((tb) => (
          <button key={tb.key} onClick={() => setTab(tb.key)} className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-bold flex items-center gap-1.5 transition-colors ${tab === tb.key ? "bg-urban-teal text-white" : "bg-white text-urban-teal shadow-warm"}`} data-testid={`admin-tab-${tb.key}`}><tb.Icon size={15} /> {tb.label}</button>
        ))}
      </div>

      {tab === "fiches" && (<>
      <div className="grid md:grid-cols-2 gap-6 mt-6">
        {/* Manual create */}
        <div className="bg-white rounded-2xl shadow-warm p-5">
          <h2 className="font-display font-bold text-lg text-urban-teal mb-3 flex items-center gap-2"><Plus size={18} /> Créer une fiche non gérée</h2>
          <div className="grid grid-cols-2 gap-3">
            <input value={newT.name} onChange={(e) => setNewT({ ...newT, name: e.target.value })} placeholder="Nom" className="afld" data-testid="admin-new-name" />
            <AddressAutocomplete value={newT.city} onChange={(v) => setNewT({ ...newT, city: v })} onSelect={(s) => setNewT({ ...newT, city: s.city || s.label })} placeholder="Ville" testid="admin-new-city" Icon={MapPin} cityOnly />
            <select value={newT.cuisine} onChange={(e) => setNewT({ ...newT, cuisine: e.target.value })} className="afld" data-testid="admin-new-cuisine">{CUISINES.map((c) => <option key={c}>{c}</option>)}</select>
            <input value={newT.photo} onChange={(e) => setNewT({ ...newT, photo: e.target.value })} placeholder="URL photo (option)" className="afld" />
          </div>
          <textarea value={newT.description} onChange={(e) => setNewT({ ...newT, description: e.target.value })} placeholder="Description" rows={2} className="afld w-full mt-3" />
          <Button onClick={create} className="rounded-full bg-street-gold text-white font-bold mt-3" data-testid="admin-create-btn">Créer (en attente)</Button>
        </div>

        {/* CSV import */}
        <div className="bg-white rounded-2xl shadow-warm p-5">
          <h2 className="font-display font-bold text-lg text-urban-teal mb-3 flex items-center gap-2"><Upload size={18} /> Importer un CSV</h2>
          <p className="text-sm text-urban-teal/60">Colonnes reconnues : <code className="bg-cream-bg px-1 rounded">name, city, cuisine, description</code>. Chaque ligne crée une fiche en attente.</p>
          <label className="mt-4 flex items-center justify-center gap-2 border-2 border-dashed border-urban-teal/30 rounded-xl py-8 cursor-pointer text-urban-teal/60 hover:border-street-gold" data-testid="admin-csv-label">
            <Upload size={20} /> Choisir un fichier CSV
            <input type="file" accept=".csv" onChange={importCsv} className="hidden" data-testid="admin-csv-input" />
          </label>
        </div>
      </div>

      {/* Management table */}
      <div className="bg-white rounded-2xl shadow-warm mt-6 overflow-hidden">
        <div className="p-5 border-b border-cream-bg flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display font-bold text-lg text-urban-teal">Fiches ({shown.length})</h2>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="afld" data-testid="admin-status-filter">
            <option value="all">Toutes</option>
            <option value="pending">En attente (à valider)</option>
            <option value="published">Publiées</option>
            <option value="unpublished">Dépubliées</option>
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-cream-bg text-urban-teal/60 text-left"><tr>
              <th className="p-3 font-semibold">Nom</th><th className="p-3 font-semibold">Ville</th><th className="p-3 font-semibold">Cuisine</th><th className="p-3 font-semibold">Type</th><th className="p-3 font-semibold">Statut</th><th className="p-3 font-semibold text-right">Actions</th>
            </tr></thead>
            <tbody>
              {shown.map((t) => (
                <tr key={t.id} className="border-t border-cream-bg" data-testid={`admin-row-${t.slug}`}>
                  <td className="p-3 font-semibold text-urban-teal">{t.name}</td>
                  <td className="p-3 text-urban-teal/70">{t.city}</td>
                  <td className="p-3 text-urban-teal/70">{t.cuisine}</td>
                  <td className="p-3">{t.managed ? <span className="text-fresh-mint font-semibold">Gérée</span> : <span className="text-urban-teal/40">Non gérée</span>}</td>
                  <td className="p-3"><span className={`px-2 py-1 rounded-full text-xs font-bold ${STATUS_STYLE[t.status]}`}>{STATUS_LABEL[t.status]}</span></td>
                  <td className="p-3">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => setEdit(t)} title="Modifier" className="p-2 rounded-lg hover:bg-cream-bg text-urban-teal" data-testid={`admin-edit-${t.slug}`}><Pencil size={15} /></button>
                      {t.status !== "published" ? (
                        <button onClick={() => setStatus(t, "published")} title="Publier" className="p-2 rounded-lg hover:bg-fresh-mint/10 text-fresh-mint" data-testid={`admin-publish-${t.slug}`}><Check size={15} /></button>
                      ) : (
                        <button onClick={() => setStatus(t, "unpublished")} title="Dépublier" className="p-2 rounded-lg hover:bg-coral-twist/10 text-coral-twist" data-testid={`admin-unpublish-${t.slug}`}><EyeOff size={15} /></button>
                      )}
                      <button onClick={() => del(t)} title="Supprimer" className="p-2 rounded-lg hover:bg-coral-twist/10 text-coral-twist" data-testid={`admin-delete-${t.slug}`}><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </>)}

      {tab === "imports" && (
        <div className="bg-white rounded-2xl shadow-warm mt-6 overflow-hidden" data-testid="imports-panel">
          <div className="p-5 border-b border-cream-bg"><h2 className="font-display font-bold text-lg text-urban-teal">Historique des imports CSV ({imports.length})</h2></div>
          {imports.length === 0 ? <p className="p-5 text-urban-teal/50 text-sm">Aucun import pour l'instant. Importez un CSV depuis l'onglet « Fiches & Imports ».</p> : (
            <table className="w-full text-sm">
              <thead className="bg-cream-bg text-urban-teal/60 text-left"><tr><th className="p-3 font-semibold">Fichier</th><th className="p-3 font-semibold">Fiches créées</th><th className="p-3 font-semibold">Par</th><th className="p-3 font-semibold">Date</th></tr></thead>
              <tbody>
                {imports.map((b) => (
                  <tr key={b.id} className="border-t border-cream-bg" data-testid="import-row">
                    <td className="p-3 font-semibold text-urban-teal">{b.filename}</td>
                    <td className="p-3"><span className="px-2 py-1 rounded-full bg-street-gold/15 text-street-gold font-bold text-xs">{b.count} en attente</span></td>
                    <td className="p-3 text-urban-teal/70">{b.admin_email}</td>
                    <td className="p-3 text-urban-teal/70">{fmt(b.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "users" && (
        <div className="bg-white rounded-2xl shadow-warm mt-6 overflow-hidden" data-testid="users-panel">
          <div className="p-5 border-b border-cream-bg"><h2 className="font-display font-bold text-lg text-urban-teal">Espaces clients ({users.length})</h2></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-cream-bg text-urban-teal/60 text-left"><tr><th className="p-3 font-semibold">Nom</th><th className="p-3 font-semibold">Email</th><th className="p-3 font-semibold">Rôle</th><th className="p-3 font-semibold">Foodtruck</th><th className="p-3 font-semibold">Forfait</th></tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-cream-bg" data-testid={`user-row-${u.email}`}>
                    <td className="p-3 font-semibold text-urban-teal">{u.name}</td>
                    <td className="p-3 text-urban-teal/70">{u.email}</td>
                    <td className="p-3"><span className="capitalize text-urban-teal/70">{u.role === "foodie" ? "Gourmand" : u.role === "owner" ? "Gérant" : "Admin"}</span></td>
                    <td className="p-3">{u.truck ? <Link to={`/foodtruck/${u.truck.slug}`} className="text-street-gold font-semibold">{u.truck.name}</Link> : <span className="text-urban-teal/30">-</span>}</td>
                    <td className="p-3">
                      {u.role === "owner" ? (
                        <select value={u.plan || "free"} onChange={(e) => setPlan(u.id, e.target.value)} className={`rounded-full px-2 py-1 text-xs font-bold border-0 ${PLAN_STYLE[u.plan] || PLAN_STYLE.free}`} data-testid={`plan-select-${u.email}`}>
                          <option value="free">Essentiel</option><option value="pro">Pro</option><option value="proplus">Pro+</option>
                        </select>
                      ) : <span className="text-urban-teal/30">-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "notifications" && (
        <div className="grid lg:grid-cols-2 gap-6 mt-6" data-testid="notifications-panel">
          <div className="bg-white rounded-2xl shadow-warm p-5">
            <h2 className="font-display font-bold text-lg text-urban-teal flex items-center gap-2 mb-3"><Send size={18} /> Envoyer une notification</h2>
            <label className="text-xs font-bold uppercase text-urban-teal/50">Destinataires</label>
            <select value={bc.audience} onChange={(e) => setBc({ ...bc, audience: e.target.value })} className="afld w-full mt-1 mb-3" data-testid="broadcast-audience">
              <option value="foodies">Tous les gourmands</option>
              <option value="truck">Abonnés d'un foodtruck</option>
            </select>
            {bc.audience === "truck" && (
              <select value={bc.slug} onChange={(e) => setBc({ ...bc, slug: e.target.value })} className="afld w-full mb-3" data-testid="broadcast-slug">
                <option value="">- Choisir un foodtruck -</option>
                {trucks.filter((t) => t.status === "published").map((t) => <option key={t.slug} value={t.slug}>{t.name}</option>)}
              </select>
            )}
            <input value={bc.subject} onChange={(e) => setBc({ ...bc, subject: e.target.value })} placeholder="Sujet" className="afld w-full mb-3" data-testid="broadcast-subject" />
            <textarea value={bc.message} onChange={(e) => setBc({ ...bc, message: e.target.value })} rows={4} placeholder="Votre message..." className="afld w-full mb-3" data-testid="broadcast-message" />
            <Button onClick={sendBroadcast} className="rounded-full bg-street-gold text-white font-bold gap-1" data-testid="broadcast-send"><Send size={15} /> Envoyer</Button>
          </div>
          <div className="bg-white rounded-2xl shadow-warm overflow-hidden">
            <div className="p-5 border-b border-cream-bg"><h2 className="font-display font-bold text-lg text-urban-teal">Journal des notifications ({notifs.length})</h2></div>
            {notifs.length === 0 ? <p className="p-5 text-urban-teal/50 text-sm">Aucune notification envoyée.</p> : (
              <div className="max-h-[420px] overflow-y-auto">
                {notifs.map((n) => (
                  <div key={n.id} className="p-4 border-b border-cream-bg" data-testid="notif-row">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-urban-teal">{n.type === "location" ? `📍 ${n.truck_name}` : n.subject}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-cream-bg text-urban-teal/60">{n.type === "location" ? "Emplacement" : "Diffusion"}</span>
                    </div>
                    <p className="text-xs text-urban-teal/60 mt-1">{n.type === "location" ? `Nouvel emplacement : ${n.location}` : `Cible : ${n.audience === "foodies" ? "Gourmands" : n.slug}`} · {n.recipients} destinataire(s) · {fmt(n.created_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit modal */}
      {edit && (
        <div className="fixed inset-0 bg-urban-teal/40 flex items-center justify-center z-50 p-4" onClick={() => setEdit(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold text-lg text-urban-teal mb-4">Modifier la fiche</h3>
            <div className="space-y-3">
              <input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="afld w-full" data-testid="admin-edit-name" />
              <AddressAutocomplete value={edit.city} onChange={(v) => setEdit({ ...edit, city: v })} onSelect={(s) => setEdit({ ...edit, city: s.city || s.label })} placeholder="Ville" testid="admin-edit-city" Icon={MapPin} cityOnly />
              <select value={edit.cuisine} onChange={(e) => setEdit({ ...edit, cuisine: e.target.value })} className="afld w-full">{CUISINES.map((c) => <option key={c}>{c}</option>)}</select>
              <textarea value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} rows={3} className="afld w-full" />
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={saveEdit} className="rounded-full bg-street-gold text-white font-bold flex-1" data-testid="admin-save-edit">Enregistrer</Button>
              <Button onClick={() => setEdit(null)} variant="ghost" className="rounded-full">Annuler</Button>
            </div>
          </div>
        </div>
      )}

      <style>{`.afld{border:2px solid #FBF7EA;border-radius:12px;padding:10px 12px;outline:none;color:#0E3F5B}.afld:focus{border-color:#0E3F5B}`}</style>
    </div>
  );
}
